import { use, useInsertionEffect, useLayoutEffect, useRef, useState } from 'react';
import { isBrowser } from '../utils.js';
export default function useSuspenseResource(request, onError) {
    const notifiedResource = useRef(undefined);
    const [previousResource, setResource] = useState(() => request === null || request === void 0 ? void 0 : request.cache.getOrCreate(request.key, request.load));
    let resource = previousResource;
    // Committed state owns the load; initial Suspense retries share the cache.
    if (request ? !(resource === null || resource === void 0 ? void 0 : resource.matches(request.key)) : resource) {
        resource = request === null || request === void 0 ? void 0 : request.cache.getOrCreate(request.key, request.load);
        setResource(resource);
    }
    // Ownership lasts until unmount. Insertion Effects stay connected while
    // Suspense or Activity hides a viewer and preserves its component state.
    useInsertionEffect(() => {
        if (!resource) {
            return;
        }
        return resource.retain();
    }, [resource]);
    const result = resource ? use(resource.promise) : undefined;
    // Commit ownership before the boundary handles the failure. Its unmount then
    // releases the failed load, allowing a normal boundary reset to retry it.
    useLayoutEffect(() => {
        if (result === null || result === void 0 ? void 0 : result.error) {
            if (onError && notifiedResource.current !== resource) {
                notifiedResource.current = resource;
                onError(result.error);
            }
            throw result.error;
        }
    }, [onError, resource, result]);
    // Server rendering has no commit phase to propagate the error.
    if (!isBrowser && (result === null || result === void 0 ? void 0 : result.error)) {
        throw result.error;
    }
    return result === null || result === void 0 ? void 0 : result.value;
}
